import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";
import { SelectDropdown, Button, Modal } from "@toddle-edu/ds-web";
import classes from "./LanguageTimezoneModal.scss";
import {
  getLanguageSelectorOptions,
  getTimezoneList,
  getGlobalTimezone,
} from "Utils";
import { getSettingValue } from "modules/PermissionModule";
import { changeLocalesLocally } from "GlobalHeader/modules/Module";
import { updateSettings } from "modules/ConfigurationThunks";
import { getUserId } from "modules/CommonSelectors";
import moment from "moment-timezone";
import { compose, graphql } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { getGlobalConstantsQuery } from "OperationalDaysAndTimeZone/modules/OperationalDaysAndTimeZoneQueries";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getGlobalConstantsFromCache } from "OperationalDaysAndTimeZone/modules/OperationalDaysAndTimeZoneGraphqlHelpers";

const LanguageTimezoneModal = ({
  languageOptions,
  timezoneOptions,
  languageValue,
  timezoneValue,
  onModalClose,
  testId,
  updateSettings,
  userId,
  changeLocalesLocally,
  i18n,
  t,
  organizationTimezone,
  setToastMsg,
  localTimezone,
}) => {
  const [language, setLanguage] = useState(languageValue);
  const [timezone, setTimezone] = useState(timezoneValue);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const onSave = async () => {
    try {
      setFullScreenLoader(true);
      let isChanged = false;
      const settings = [
        {
          name: "globalTimezoneConfig",
          value: JSON.stringify({
            value: timezone.value,
            localTimezone,
          }),
        },
      ];
      const isTimezoneChanged = !_.isEqual(timezone, timezoneValue);
      if (!_.isEqual(language, languageValue)) {
        isChanged = true;
        await changeLocalesLocally({
          value: language.value,
          userId,
          i18n,
          showToastMsg: false,
        });
      }
      if (isTimezoneChanged) {
        if (isTimezoneChanged) {
          moment.tz.setDefault(timezone.value);
        }
        isChanged = true;
        localStorage.setItem("showTimezoneAlertModal", "false");
        await updateSettings({
          id: userId,
          type: "user",
          settings,
          showToastMsg: false,
        });
      }
      if (isChanged) {
        setToastMsg({
          type: "success",
          msg: "toastMsgs:saved_successfully",
        });
      }
      setFullScreenLoader(false);
      onModalClose();
    } catch (error) {
      setFullScreenLoader(false);
      onModalClose();
      setToastMsg({
        msg: "toastMsgs:something_went_wrong",
        type: "warning",
      });
    }
  };

  const showResetOption = !_.isEqual(
    organizationTimezone.value,
    timezone.value
  );
  return (
    <>
      <Modal
        hasOverlay={true}
        isOpen={true}
        dsVersion={"2.0"}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        width={"432px"}
        height={"432px"}
        onClose={onModalClose}
      >
        <Modal.Header
          testId={`${testId}-languageTimezoneModal-header`}
          isClosable={true}
          hasSeparator={true}
          title={t("common:edit_language_and_timezone")}
        />
        <Modal.Body isScrollable={true}>
          <div className={classes.container}>
            <div className={classes.languageContainer}>
              <label className="text-label-2">{t("common:language")}</label>
              <SelectDropdown
                testId={`${testId}-languageTimezoneModal-language`}
                name="layout"
                value={language}
                onChange={value => setLanguage(value)}
                options={languageOptions}
                isClearable={false}
              />
            </div>
            <div className={classes.timezoneContainer}>
              <div className={classes.labelContainer}>
                <label className="text-label-2">{t("common:timezone")}</label>
                {showResetOption ? (
                  <Button
                    dsVersion={"2.0"}
                    type="inline"
                    variant="progressive"
                    onClick={() => setTimezone(organizationTimezone)}
                  >
                    {t("common:reset_to_school_time")}
                  </Button>
                ) : null}
              </div>
              <SelectDropdown
                testId={`${testId}-languageTimezoneModal-timezone`}
                name="layout"
                value={timezone}
                onChange={value => setTimezone(value)}
                options={timezoneOptions}
                isClearable={false}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer hasSeparator={true}>
          <div className={classes.buttonsContainer}>
            <Button
              onClick={onModalClose}
              dsVersion={"2.0"}
              type={"outlined"}
              variant={"neutral"}
              testId={`${testId}-downloadPlannerModal-cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              dsVersion={"2.0"}
              onClick={onSave}
              type={"fill"}
              variant="progressive"
              shortcut={{
                code: "Enter",
                shiftKey: true,
              }}
              testId={`${testId}-downloadPlannerModal-save`}
            >
              {t("common:save")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const mapActionCreators = {
  updateSettings,
  changeLocalesLocally,
  setToastMsg,
};

const mapStateToProps = state => {
  const languageOptions = _.map(
    getLanguageSelectorOptions(),
    ({ key, ...rest }) => ({
      value: key,
      ...rest,
    })
  );
  const timezoneOptions = getTimezoneList();
  const userId = getUserId(state);
  const organizationId = _.get(state, "login.userInfo.org_id");
  const timezoneConfig = getSettingValue({
    name: "globalTimezoneConfig",
    userId,
    readFromUserAndOrgLevel: true,
  });
  const languageValue = _.find(languageOptions, {
    value: getSettingValue({
      name: "locale",
      userId,
      readFromUserAndOrgLevel: true,
    }),
  });
  const organizationTimezone = getGlobalConstantsFromCache({
    id: organizationId,
    filter: {
      names: ["TIMEZONE"],
    },
  });
  const organizationTimezoneValue = _.get(
    _.find(
      _.get(organizationTimezone, "globalConstants", []),
      ({ name }) => name === "TIMEZONE"
    ) ?? {},
    "value",
    getGlobalTimezone()
  );
  const timezoneConfigValue = _.get(timezoneConfig, "value", "UNDEFINED");
  let tempCurrentTimezoneValue;
  let mode = "CUSTOM";
  switch (timezoneConfigValue) {
    case "ORG":
    case "UNDEFINED":
      tempCurrentTimezoneValue = organizationTimezoneValue;
      mode = timezoneConfigValue;
      break;
    // may need this in future
    // case "AUTO":
    //   tempCurrentTimezoneValue = moment.tz.guess();
    //   mode = timezoneConfigValue;
    //   break;

    default:
      tempCurrentTimezoneValue = timezoneConfigValue;
      break;
  }
  const timezoneValue = {
    ..._.find(timezoneOptions, {
      value: tempCurrentTimezoneValue,
    }),
    mode,
  };
  const timeZoneQueryVariables = {
    id: organizationId,
    filter: {
      names: ["TIMEZONE"],
    },
  };

  const localTimezone = moment.tz.guess();

  return {
    userId,
    languageOptions,
    timezoneOptions,
    languageValue,
    timezoneValue,
    isLoading: false,
    timeZoneQueryVariables,
    localTimezone,
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getGlobalConstantsQuery, {
    name: "getTimeZone",
    options: ({ timeZoneQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: timeZoneQueryVariables,
    }),
    props: ({ getTimeZone, ownProps }) => {
      const { isLoading, timezoneOptions } = ownProps;
      const { networkStatus } = getTimeZone;
      const data = _.get(getTimeZone, "node", {});

      const timeZone =
        _.find(
          _.get(data, "globalConstants", []),
          ({ name }) => name === "TIMEZONE"
        ) ?? {};

      const organizationTimezone = _.find(timezoneOptions, {
        value: _.get(timeZone, "value", getGlobalTimezone()),
      });

      const isData = !_.isEmpty(data);

      return {
        isData: !_.isEmpty(data),
        isLoading:
          _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) ||
          isLoading,
        ...(isData ? { organizationTimezone } : {}),
      };
    },
  })
)(LanguageTimezoneModal);
